import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer
} from '@angular/core';
import {environment} from '@env/environment';
import {bootstrapApplication} from "@angular/platform-browser";
import {AppComponent} from "@app/app.component";
import {provideRouter, RouteReuseStrategy} from "@angular/router";
import {IonicRouteStrategy, provideIonicAngular} from "@ionic/angular/standalone";
import {routes} from "./app.routes";
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr} from "@angular/common/http";
import {provideServiceWorker} from '@angular/service-worker';
import {registerLocaleData} from "@angular/common";
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import {provideIcons} from "@ng-icons/core";
import {
  heroAdjustmentsHorizontal,
  heroBell,
  heroBellAlert,
  heroBellSlash,
  heroBookmark,
  heroCalendar,
  heroCalendarDays,
  heroChartBar,
  heroChatBubbleLeftRight,
  heroChevronLeft,
  heroChevronRight,
  heroClock,
  heroEnvelope,
  heroEye,
  heroEyeSlash,
  heroHeart,
  heroMagnifyingGlass,
  heroMagnifyingGlassCircle,
  heroMapPin,
  heroNewspaper,
  heroPencilSquare,
  heroPhone,
  heroPlus,
  heroShare,
  heroUser,
  heroXMark,
} from "@ng-icons/heroicons/outline";
import {heroHeartSolid} from "@ng-icons/heroicons/solid";
import {AuthInterceptor} from "@app/interceptors/auth-interceptor";
import {LoadingInterceptor} from "@app/interceptors/loading-interceptor";
import {IonicStorageModule} from "@ionic/storage-angular";
import {defineCustomElements} from '@ionic/pwa-elements/loader';
import {A2hsService, StorageService} from "@app/services";
import {ErrorHandlerDebug} from "@app/utils/error-handler-debug";
import {NgxSeoModule} from "@avivharuzi/ngx-seo";
import {enableElfProdMode} from "@ngneat/elf";
import {ErrorHandlerFlare} from "@app/utils/error-handler-flare";

registerLocaleData(localeDe, 'de-DE', localeDeExtra);
defineCustomElements(window);

if (environment.production) {
  enableProdMode();
  enableElfProdMode();
  if (window) {
    window.console.log = function () {
    };
  }
}

const ERROR_HANDLER = environment.production ? ErrorHandlerFlare : ErrorHandlerDebug;

bootstrapApplication(AppComponent, {
  providers: [
    {provide: ErrorHandler, useClass: ERROR_HANDLER},
    {provide: RouteReuseStrategy, useClass: IonicRouteStrategy},
    provideAppInitializer(() => {
      const initializerFn = (appInitializer)(inject(StorageService), inject(A2hsService));
      return initializerFn();
    }),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true
    },
    provideIonicAngular(),
    provideRouter(routes),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:5000'
    }),
    importProvidersFrom(IonicStorageModule.forRoot({
      name: '89284-app-db',
    })),
    importProvidersFrom(NgxSeoModule.forRoot()),
    provideIcons({
      heroAdjustmentsHorizontal,
      heroBookmark,
      heroCalendar,
      heroCalendarDays,
      heroClock,
      heroHeart,
      heroHeartSolid,
      heroMapPin,
      heroNewspaper,
      heroUser,
      heroPlus,
      heroMagnifyingGlass,
      heroMagnifyingGlassCircle,
      heroPhone,
      heroEnvelope,
      heroShare,
      heroBell,
      heroBellAlert,
      heroBellSlash,
      heroEye,
      heroEyeSlash,
      heroXMark,
      heroChevronLeft,
      heroChevronRight,
      heroPencilSquare,
      heroChatBubbleLeftRight,
      heroChartBar,
    })
  ]
});

export function appInitializer(storageService: StorageService, a2hsService: A2hsService) {
  return async () => {
    console.log('appInitializer');
    await storageService.init();

    console.log(`appInitializer: registerPwaInstallEventListener`);

    a2hsService.checkUserAgent();
    a2hsService.trackStandalone();

    window.addEventListener('beforeinstallprompt', (e) => {
      console.log(`appInitializer.registerPwaInstallEventListener.beforeinstallprompt`, e)
      a2hsService.captureInstallPrompt(e);
    });
    window.addEventListener('appinstalled', (evt) => {
      a2hsService.trackInstalled();
    });
  }
}
